import { apiClient } from "./client";
import type {
  MyTaskListParams,
  MyTaskListResponse,
  MyTaskRead,
  TaskCreate,
  TaskListParams,
  TaskListResponse,
  TaskRead,
  TaskStatusConfig,
  TaskStatusesResponse,
  TaskSummary,
  TaskUpdate,
} from "./types";

/** Cross-project task feed for the current user — the backend joins
 *  across every project they belong to and stamps each row with its
 *  project slug + name. Powers the cross-project "My tasks" scope and
 *  the dashboard card. `status` is a comma-separated list of
 *  kanban-lane keys (e.g. "todo,in_progress" for the open pile). The
 *  backend caps `page_size` at 200. */
export async function getMyTasks(
  params: MyTaskListParams = {},
): Promise<MyTaskListResponse> {
  const res = await apiClient.get<MyTaskListResponse>("/me/tasks", {
    params: {
      status: params.status || undefined,
      page: params.page ?? undefined,
      page_size: params.page_size ?? undefined,
    },
  });
  return res.data;
}

/** Fetch every page of the cross-project /me/tasks feed (it caps
 *  page_size at 200), bounded by `maxPages` so a pathological backlog
 *  can't fan out into dozens of requests. Returns the merged item list
 *  — callers do their own client-side sorting / KPI math on it. */
export async function getAllMyTasks(
  params: { status?: string } = {},
  maxPages = 10,
): Promise<MyTaskRead[]> {
  const PAGE = 200;
  const first = await getMyTasks({ ...params, page_size: PAGE, page: 1 });
  let items = first.items;
  const totalPages = Math.min(Math.ceil((first.total || 0) / PAGE), maxPages);
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        getMyTasks({ ...params, page_size: PAGE, page: i + 2 }),
      ),
    );
    for (const r of rest) items = items.concat(r.items);
  }
  return items;
}

/**
 * Lists the configured task statuses for the project. The ordered list
 * drives the kanban columns + the default status for new tasks. When
 * the backend hasn't shipped this endpoint yet (or the project was
 * never configured), the caller should fall back to the legacy
 * is_completed two-column behavior.
 */
export async function getTaskStatuses(
  slug: string,
): Promise<TaskStatusConfig[]> {
  const res = await apiClient.get<TaskStatusesResponse>(
    `/projects/${slug}/tasks/statuses`,
  );
  return res.data.statuses ?? [];
}

function buildParams(params: TaskListParams): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    cleaned[key] = value;
  }
  return cleaned;
}

export async function listTasks(
  slug: string,
  params: TaskListParams = {},
): Promise<TaskListResponse> {
  const res = await apiClient.get<TaskListResponse>(
    `/projects/${slug}/tasks`,
    { params: buildParams(params) },
  );
  return res.data;
}

export async function getTaskSummary(
  slug: string,
  params: { assigned_to?: string } = {},
): Promise<TaskSummary> {
  const res = await apiClient.get<TaskSummary>(
    `/projects/${slug}/tasks/summary`,
    { params },
  );
  return res.data;
}

export async function createTask(
  slug: string,
  data: TaskCreate,
): Promise<TaskRead> {
  const res = await apiClient.post<TaskRead>(
    `/projects/${slug}/tasks`,
    data,
  );
  return res.data;
}

export async function updateTask(
  slug: string,
  id: number,
  data: TaskUpdate,
): Promise<TaskRead> {
  const res = await apiClient.patch<TaskRead>(
    `/projects/${slug}/tasks/${id}`,
    data,
  );
  return res.data;
}

export async function deleteTask(slug: string, id: number): Promise<void> {
  await apiClient.delete(`/projects/${slug}/tasks/${id}`);
}

export async function completeTask(
  slug: string,
  id: number,
): Promise<TaskRead> {
  const res = await apiClient.post<TaskRead>(
    `/projects/${slug}/tasks/${id}/complete`,
  );
  return res.data;
}

export async function reopenTask(
  slug: string,
  id: number,
): Promise<TaskRead> {
  const res = await apiClient.post<TaskRead>(
    `/projects/${slug}/tasks/${id}/reopen`,
  );
  return res.data;
}
